import { supabase } from "@/lib/supabaseClient";

/** Possible expiry states for a signature book */
export type ExpiryStatus = "none" | "active" | "soon" | "expired";

/**
 * Derive the expiry status from `expires_at`.
 * - "none"    → no expires_at set
 * - "active"  → expires_at in the future (> 14 days away)
 * - "soon"    → expires within the next 14 days
 * - "expired" → expires_at is in the past
 */
export function getExpiryStatus(expiresAt: string | null): ExpiryStatus {
  if (!expiresAt) return "none";
  const exp  = new Date(expiresAt);
  const now  = new Date();
  const diff = exp.getTime() - now.getTime();

  if (diff < 0)                          return "expired";
  if (diff < 14 * 24 * 60 * 60 * 1000)  return "soon";
  return "active";
}

/**
 * Returns a human-readable label + Tailwind colour classes for a given status.
 */
export function getExpiryBadge(status: ExpiryStatus): {
  label: string;
  className: string;
  icon: string;
} {
  switch (status) {
    case "active":
      return { label: "Vigente",    className: "bg-emerald-100 text-emerald-700", icon: "ri-checkbox-circle-line"  };
    case "soon":
      return { label: "Vence pronto", className: "bg-amber-100 text-amber-700",  icon: "ri-time-line"              };
    case "expired":
      return { label: "Vencido",    className: "bg-red-100 text-red-600",         icon: "ri-close-circle-line"     };
    case "none":
    default:
      return { label: "",           className: "",                                icon: ""                         };
  }
}

/**
 * Activate a signature book:
 * - sets activated_at = now()
 * - sets expires_at   = now + 3 months
 *
 * Returns the updated row or throws an error.
 */
export async function activateSignatureBook(
  bookId: string,
  tenantId: string,
): Promise<{ activated_at: string; expires_at: string }> {
  const now       = new Date();
  const expiresAt = new Date(now);
  expiresAt.setMonth(expiresAt.getMonth() + 3);

  const { data, error } = await supabase
    .from("signature_books")
    .update({
      activated_at: now.toISOString(),
      expires_at:   expiresAt.toISOString(),
      updated_at:   now.toISOString(),
    })
    .eq("id", bookId)
    .eq("tenant_id", tenantId)
    .select("activated_at, expires_at")
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) throw new Error("No se pudo activar el libro.");

  return data as { activated_at: string; expires_at: string };
}

/**
 * Renew an existing signature book for another 3 months
 * starting from the current expires_at (or now if already expired).
 */
export async function renewSignatureBook(
  bookId: string,
  tenantId: string,
  currentExpiresAt: string | null,
): Promise<{ activated_at: string; expires_at: string }> {
  const base      = currentExpiresAt && new Date(currentExpiresAt) > new Date()
    ? new Date(currentExpiresAt)
    : new Date();
  const expiresAt = new Date(base);
  expiresAt.setMonth(expiresAt.getMonth() + 3);
  const now = new Date();

  const { data, error } = await supabase
    .from("signature_books")
    .update({
      activated_at: now.toISOString(),
      expires_at:   expiresAt.toISOString(),
      updated_at:   now.toISOString(),
    })
    .eq("id", bookId)
    .eq("tenant_id", tenantId)
    .select("activated_at, expires_at")
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) throw new Error("No se pudo renovar el libro.");

  return data as { activated_at: string; expires_at: string };
}